// ═══════════════════════════════════════════════════════════════
// MetaSummaryCard — Level3 Méta-résumé de corpus
// ═══════════════════════════════════════════════════════════════
//
// Affiche et permet de générer le méta-résumé d'une base.
// Level3 = synthèse transversale de tous les level2 des documents.
//
// Props :
//   - baseId: number
//   - baseName: string
//   - metaSummary: object|null — from expertise_bases.meta_summary
//   - documentCount: number — documents done dans la base
//   - isOwner: boolean
//   - onGenerated: function — callback après génération
// ═══════════════════════════════════════════════════════════════

import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import {
    BookOpen, Loader, RefreshCw, Sparkles, Copy, Check, ChevronDown, ChevronUp
} from 'lucide-react';

const API_BASE = '/api/expertise-bases';
const getToken = () => localStorage.getItem('oracle_token');
const authHeaders = () => ({
    Authorization: `Bearer ${getToken()}`,
    'Content-Type': 'application/json',
});

const ACCENT = '#8b5cf6';

export default function MetaSummaryCard({ baseId, baseName, metaSummary, documentCount, isOwner, onGenerated }) {
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(true);
    const [copied, setCopied] = useState(false);
    const [localMeta, setLocalMeta] = useState(metaSummary || null);

    const meta = localMeta;
    const hasSummary = meta && meta.summary;
    const canGenerate = documentCount >= 2;

    const handleGenerate = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/${baseId}/meta-summary`, {
                method: 'POST',
                headers: authHeaders(),
            });
            const data = await res.json();
            if (data.success) {
                setLocalMeta(data.meta_summary);
                setExpanded(true);
                if (onGenerated) onGenerated(data.meta_summary);
            }
        } catch (err) {
            console.error('Meta-summary generation failed:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = () => {
        if (meta?.summary) {
            navigator.clipboard.writeText(meta.summary);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    return (
        <div style={{
            background: 'var(--card-bg)',
            border: '1px solid var(--card-border)',
            borderRadius: 14, padding: '18px 20px',
        }}>
            {/* Header */}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                cursor: hasSummary ? 'pointer' : 'default',
            }}
                onClick={() => hasSummary && setExpanded(!expanded)}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                        width: 32, height: 32, borderRadius: 10,
                        background: `linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(59, 130, 246, 0.08))`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <BookOpen size={16} style={{ color: '#10b981' }} />
                    </div>
                    <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                            Synthèse du corpus
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                            {hasSummary
                                ? `${meta.documents_used} document(s) · ${meta.summary.length} chars · ${meta.model || 'LLM'}`
                                : `Level3 — résumé transversal de ${documentCount} document(s)`
                            }
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {hasSummary && (
                        <>
                            <button
                                onClick={(e) => { e.stopPropagation(); handleCopy(); }}
                                style={{
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    color: copied ? '#10b981' : 'var(--text-muted)',
                                    padding: '4px', borderRadius: 6, display: 'flex',
                                }}
                                title="Copier"
                            >
                                {copied ? <Check size={14} /> : <Copy size={14} />}
                            </button>
                            {isOwner && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleGenerate(); }}
                                    disabled={loading}
                                    style={{
                                        background: 'none', border: 'none', cursor: loading ? 'wait' : 'pointer',
                                        color: ACCENT, padding: '4px', borderRadius: 6, display: 'flex',
                                        opacity: loading ? 0.5 : 1,
                                    }}
                                    title="Régénérer"
                                >
                                    {loading ? <Loader size={14} className="spin" /> : <RefreshCw size={14} />}
                                </button>
                            )}
                            <div style={{ color: 'var(--text-muted)', padding: '4px' }}>
                                {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Contenu */}
            {hasSummary && expanded && (
                <div style={{
                    marginTop: 14, paddingTop: 14,
                    borderTop: '1px solid var(--card-border)',
                }}>
                    <div style={{
                        fontSize: 13, lineHeight: 1.7,
                        color: 'var(--text-secondary)',
                    }}
                        className="meta-summary-content"
                    >
                        <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>
                            {meta.summary}
                        </ReactMarkdown>
                    </div>

                    {/* Footer meta */}
                    <div style={{
                        marginTop: 12, paddingTop: 8,
                        borderTop: '1px solid var(--card-border)',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        fontSize: 10, color: 'var(--text-muted)',
                    }}>
                        <span>
                            {meta.generated_at && new Date(meta.generated_at).toLocaleDateString('fr-FR', {
                                day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                            })}
                            {meta.costUsd > 0 && ` · $${meta.costUsd.toFixed(4)}`}
                        </span>
                        {meta.versions?.length > 0 && (
                            <span style={{ color: ACCENT }}>
                                {meta.versions.length} version(s) précédente(s)
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* Pas encore de résumé */}
            {!hasSummary && (
                <div style={{ textAlign: 'center', padding: '14px 0 6px' }}>
                    {canGenerate ? (
                        isOwner ? (
                            <button
                                onClick={handleGenerate}
                                disabled={loading}
                                style={{
                                    background: `linear-gradient(135deg, #10b981, #10b981dd)`,
                                    color: '#fff', border: 'none', borderRadius: 10,
                                    padding: '8px 18px', fontSize: 12, fontWeight: 600,
                                    cursor: loading ? 'wait' : 'pointer',
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    opacity: loading ? 0.7 : 1,
                                    transition: 'opacity 0.2s',
                                }}
                            >
                                {loading ? <Loader size={14} className="spin" /> : <Sparkles size={14} />}
                                {loading ? 'Synthèse en cours...' : 'Synthétiser le corpus'}
                            </button>
                        ) : (
                            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
                                Le propriétaire peut générer la synthèse du corpus
                            </p>
                        )
                    ) : (
                        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
                            Minimum 2 documents avec synthèse pour générer le méta-résumé
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
